import type { ThemeConfig } from 'antd';

import { MENU_TOKENS } from './components/Sidebar/Menu/Menu.styled';

const FONTS = {
  fontFamily: "'Albert Sans', sans-serif",
  fontSize: 15,
  fontSizeLG: 17,
  fontSizeSM: 13,
  fontSizeHeading3: 25,
  fontSizeHeading4: 21,
  fontSizeHeading5: 17,
  fontWeightStrong: 500,
};

const MARGINS = {
  marginXS: 8,
};

const PADDINGS = {
  paddingXS: 8,
  paddingSM: 12,
};

const BORDER_RADIUS = {
  borderRadiusSM: 4,
};

const BOX_SHADOWS = {
  boxShadowSecondary:
    '0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);',
};

const DEFAULT_SETTINGS = {
  ...MARGINS,
  ...PADDINGS,
  ...BORDER_RADIUS,
  ...FONTS,
  ...BOX_SHADOWS,
};

export const lightTheme: ThemeConfig = {
  token: {
    colorPrimary: '#1bb5aa',
    colorPrimaryText: '#1BB5AA',
    colorBgLayout: '#f4f4f4',
    colorBgContainer: '#ffffff',
    colorSuccessBgHover: '#D5F5E1',
    colorErrorBgHover: '#FFEAEA',
    colorTextDescription: '#00000073',
    colorSuccessBg: '#F0FFF5',
    colorSuccessHover: '#79DBA3',
    colorSuccess: '#2cc277',
    colorWarning: '#ffba00',
    colorError: '#f54761',
    colorBgBase: '#ffffff',
    colorBgElevated: '#ffffff',
    colorSplit: 'rgba(0, 0, 0, 0.06)',
    colorPrimaryBorder: '#8adbce',
    colorBorder: '#E2E2E2',
    colorBorderSecondary: '#F0F0F0',
    controlItemBgActive: '#e9f9f5',
    colorFillSecondary: '#F0F0F0',
    colorFillQuaternary: 'rgba(0, 0, 0, 0.02)',
    colorTextBase: '#000000',
    colorTextHeading: 'rgba(0, 0, 0, 0.88)',
    colorTextDisabled: 'rgba(0, 0, 0, 0.25)',
    colorTextPlaceholder: 'rgba(191, 191, 191, 1)',
    colorText: 'rgba(0, 0, 0, 0.88)',
    colorTextLabel: '#595959',
    colorTextSecondary: 'rgba(89, 89, 89, 1)',
    colorTextTertiary: '#8C8C8C',
    colorErrorBg: '#FFF3F3',
    colorSuccessBorderHover: '#79DBA3',
    colorErrorBorder: '#FFC4C7',
    colorErrorTextActive: '#DD2F54',
    colorSuccessTextActive: '#1C9C60',
    colorSuccessText: '#2CC277',
    colorIcon: '#8C8C8C',
    controlItemBgHover: '#F5F5F5',
    colorBgMask: '#00000073',
    colorWarningActive: '#D99800',
    colorWarningBg: '#FFFCE6',
    colorWarningBgHover: '#FFF3A3',
    colorWarningBorder: '#FFE97A',
    colorWarningBorderHover: '#FFDC52',
    colorLink: '#1bb5aa',
    colorPrimaryBg: '#E9F9F5',
    colorPrimaryBgHover: 'rgba(186, 234, 226, 1)',
    colorPrimaryActive: 'rgba(14, 143, 139, 1)',
    colorFill: '#D9D9D9',
    colorInfo: 'rgba(27, 181, 170, 1)',
    colorInfoBg: 'rgba(233, 249, 245, 1)',
    colorInfoBorder: 'rgba(138, 219, 206, 1)',
    colorFillTertiary: '#F5F5F5',
    colorBgSolid: '#fafafa',
    ...DEFAULT_SETTINGS,
  },
  components: {
    Select: {
      colorTextPlaceholder: 'rgba(31, 31, 31, 1)',
    },
    Menu: MENU_TOKENS.light,
    Button: {
      ghostBg: 'rgba(255, 234, 234, 1)',
    },
    Card: {
      colorBorder: '#FFC4C7',
    },
    Pagination: {
      colorTextDisabled: '#BFBFBF',
    },
    Progress: {
      defaultColor: '#1bb5aa',
    },
  },
};

export const CARD_TOKENS = {
  light: {
    contentColor: '1F1F1F',
  },
  dark: {
    contentColor: 'DCDCDC',
  },
};
export const darkTheme: ThemeConfig = {
  token: {
    colorPrimary: '#39b2a6',
    colorPrimaryText: '#39B2A6',
    colorBgLayout: '#0B0D0E',
    colorBgContainer: '#131617',
    colorSuccessBgHover: '#153729',
    colorErrorBgHover: '#491F2A',
    colorTextDescription: '#ffffff73',
    colorText: '#DCDCDC',
    colorTextLabel: '#ACADAE',
    colorSuccessBg: '#12231F',
    colorSuccessHover: '#1E6342',
    colorSuccessBorder: '#1B4934',
    colorSuccess: '#28a869',
    colorWarning: '#dca103',
    colorError: '#da455c',
    colorBgBase: '#1a1e24',
    colorBgElevated: '#1C2021',
    colorSplit: 'rgba(255, 255, 255, 0.10)',
    colorBorder: '#3C3F44',
    colorPrimaryBorder: '#1a6260',
    colorBorderSecondary: '#313439',
    controlItemBgActive: '#133134',
    colorFillSecondary: '#2F3233',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.04)',
    colorTextBase: '#ffffff',
    colorTextHeading: 'rgba(255, 255, 255, 0.85)',
    colorTextDisabled: 'rgba(255, 255, 255, 0.25)',
    colorTextPlaceholder: 'rgba(78, 80, 81, 1)',
    colorTextSecondary: 'rgba(172, 173, 174, 1)',
    colorTextTertiary: '#7D7F7F',
    geekblue: '#ccd9eb',
    colorErrorBg: '#2B151C',
    colorSuccessBorderHover: '#1E6342',
    colorErrorBorder: '#612935',
    colorErrorTextActive: '#DA455C',
    colorSuccessTextActive: '#4BBC7F',
    colorSuccessText: '#4BBC7F',
    colorIcon: '#7D7F7F',
    controlItemBgHover: '#26292A',
    colorBgMask: '#00000073',
    colorWarningActive: '#AC8008',
    colorWarningBg: '#2B2414',
    colorWarningBgHover: '#453611',
    colorWarningBorder: '#5A4710',
    colorWarningBorderHover: '#7D5F0D',
    colorLink: '#39B2A6',
    colorPrimaryBg: '#133134',
    colorPrimaryBgHover: 'rgba(26, 75, 76, 1)',
    colorPrimaryActive: 'rgba(27, 158, 150, 1)',
    colorInfo: 'rgba(57, 178, 166, 1)',
    colorInfoBg: 'rgba(19, 49, 52, 1)',
    colorInfoBorder: 'rgba(26, 98, 96, 1)',
    colorFill: '#3D4041',
    colorFillTertiary: '#26292A',
    colorBgSolid: '#1c1f20',
    ...DEFAULT_SETTINGS,
  },
  components: {
    Select: {
      colorTextPlaceholder: 'rgba(220, 220, 220, 1)',
    },
    Menu: MENU_TOKENS.dark,
    Button: {
      ghostBg: 'rgba(73, 31, 42, 1)',
    },
    Pagination: {
      colorTextDisabled: '#4E5051',
    },
    Progress: {
      defaultColor: '#39b2a6',
    },
  },
};
